import { supabase } from './supabase.js';

/**
 * Queue Manager for background data generation
 */

// Job types enum
export const JOB_TYPES = {
  COLLEGE_DATA: 'college_data',
  DEADLINES: 'deadlines',
  ESSAY_REQUIREMENTS: 'essay_requirements',
  VALUE_PROPOSITION: 'value_proposition',
  STUDENT_ADVANTAGES: 'student_advantages',
  STRATEGIC_INSIGHTS: 'strategic_insights',
  HONORS_PROGRAMS: 'honors_programs',
  FINANCIAL_AID: 'financial_aid',
  CAMPUS_CULTURE: 'campus_culture'
};

// Job status enum
export const JOB_STATUS = {
  PENDING: 'pending',
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

/**
 * Queue a single data generation job
 */
export async function queueJob(userId, collegeId, jobType, requestData = {}) {
  try {
    const { data, error } = await supabase.rpc('queue_data_generation_job', {
      p_user_id: userId,
      p_college_id: collegeId,
      p_job_type: jobType,
      p_request_data: requestData
    });

    if (error) throw error;

    // Check if we should trigger immediate processing
    const shouldTrigger = await checkIfShouldTriggerProcessing();
    if (shouldTrigger) {
      // Fire and forget - don't wait for processing
      triggerQueueProcessing().catch(console.error);
    }

    return { success: true, jobId: data };
  } catch (error) {
    console.error('Error queuing job:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Queue multiple jobs for a college (all data types)
 */
export async function queueAllCollegeData(userId, collegeId, collegeName) {
  const jobs = [
    { type: JOB_TYPES.COLLEGE_DATA, data: { collegeName, dataFields: 'all' } },
    { type: JOB_TYPES.DEADLINES, data: { collegeName } },
    { type: JOB_TYPES.ESSAY_REQUIREMENTS, data: { collegeName } },
    { type: JOB_TYPES.VALUE_PROPOSITION, data: { collegeName } },
    { type: JOB_TYPES.STUDENT_ADVANTAGES, data: { collegeName } },
    { type: JOB_TYPES.STRATEGIC_INSIGHTS, data: { collegeName } },
    { type: JOB_TYPES.CAMPUS_CULTURE, data: { collegeName, dataFields: 'culture' } }
  ];

  const results = [];
  for (const job of jobs) {
    const result = await queueJob(userId, collegeId, job.type, job.data);
    results.push({ ...result, jobType: job.type });
  }

  return results;
}

/**
 * Regenerate college data (marks as regeneration for daily limits)
 * Checks backend flag to ensure regeneration is enabled
 */
export async function regenerateCollegeData(userId, collegeId, collegeName, jobTypes = null) {
  try {
    // Check if manual regeneration is enabled for this user
    const { data: limits, error: limitsError } = await supabase
      .from('user_processing_limits')
      .select('manual_regen_enabled')
      .eq('user_id', userId)
      .single();

    if (limitsError && limitsError.code !== 'PGRST116') {
      throw limitsError;
    }

    // If manual_regen_enabled is explicitly false, reject
    if (limits && limits.manual_regen_enabled === false) {
      return {
        success: false,
        error: 'Manual data regeneration is currently disabled. Please contact support.'
      };
    }

    // Default to regenerating core data types if not specified
    const typesToRegenerate = jobTypes || [
      JOB_TYPES.COLLEGE_DATA,
      JOB_TYPES.DEADLINES,
      JOB_TYPES.ESSAY_REQUIREMENTS
    ];

    const jobs = typesToRegenerate.map(type => ({
      type,
      data: {
        collegeName,
        is_regeneration: true, // Mark as regeneration for daily limits
        dataFields: type === JOB_TYPES.COLLEGE_DATA ? 'all' : undefined
      }
    }));

    const results = [];
    for (const job of jobs) {
      const result = await queueJob(userId, collegeId, job.type, job.data);
      results.push({ ...result, jobType: job.type });
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.length - successCount;

    return {
      success: successCount > 0,
      queued: successCount,
      failed: failCount,
      results
    };
  } catch (error) {
    console.error('Error regenerating college data:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get user's queue status
 */
export async function getUserQueueStatus(userId) {
  try {
    const { data, error } = await supabase
      .from('data_generation_queue')
      .select(`
        *,
        college:college_journey_colleges(
          id,
          name,
          global_colleges(name)
        )
      `)
      .eq('user_id', userId)
      .order('priority_score', { ascending: false })
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Group by status
    const grouped = {
      processing: [],
      queued: [],
      pending: [],
      completed: [],
      failed: []
    };

    data.forEach(job => {
      if (grouped[job.status]) {
        grouped[job.status].push(job);
      }
    });

    return { success: true, data: grouped };
  } catch (error) {
    console.error('Error fetching queue status:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get college-specific queue status
 */
export async function getCollegeQueueStatus(collegeId) {
  try {
    const { data, error } = await supabase
      .from('data_generation_queue')
      .select('*')
      .eq('college_id', collegeId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Create a map of job types to their latest status
    const statusMap = {};
    data.forEach(job => {
      if (!statusMap[job.job_type] || job.created_at > statusMap[job.job_type].created_at) {
        statusMap[job.job_type] = {
          status: job.status,
          created_at: job.created_at,
          completed_at: job.completed_at,
          error_message: job.error_message
        };
      }
    });

    return { success: true, data: statusMap };
  } catch (error) {
    console.error('Error fetching college queue status:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Cancel a job
 */
export async function cancelJob(jobId) {
  try {
    const { error } = await supabase
      .from('data_generation_queue')
      .update({ status: JOB_STATUS.CANCELLED })
      .eq('id', jobId)
      .in('status', [JOB_STATUS.PENDING, JOB_STATUS.QUEUED]);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error cancelling job:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get user's processing limits
 */
export async function getUserProcessingLimits(userId) {
  try {
    const { data, error } = await supabase
      .from('user_processing_limits')
      .select('*')
      .eq('user_id', userId)
      .eq('date', new Date().toISOString().split('T')[0])
      .single();

    if (error && error.code !== 'PGRST116') throw error; // Ignore not found error

    return {
      success: true,
      data: data || { jobs_processed: 0, daily_limit: 2 }
    };
  } catch (error) {
    console.error('Error fetching processing limits:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Subscribe to queue updates for a user
 */
export function subscribeToQueueUpdates(userId, callback) {
  const subscription = supabase
    .channel(`queue-updates-${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'data_generation_queue',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback(payload);
      }
    )
    .subscribe();

  return subscription;
}

/**
 * Calculate estimated completion time
 */
export function calculateEstimatedCompletion(queuePosition, isHighPriority = false) {
  if (isHighPriority) {
    // High priority jobs process within 1-2 hours
    const hours = Math.ceil(queuePosition * 0.5);
    return new Date(Date.now() + hours * 60 * 60 * 1000);
  } else {
    // Regular jobs process over 1-3 days
    const days = Math.ceil(queuePosition / 2);
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + days);
    targetDate.setHours(6, 0, 0, 0); // Set to 6 AM
    return targetDate;
  }
}

/**
 * Format job type for display
 */
export function formatJobType(jobType) {
  const labels = {
    [JOB_TYPES.COLLEGE_DATA]: 'Basic College Data',
    [JOB_TYPES.DEADLINES]: 'Application Deadlines',
    [JOB_TYPES.ESSAY_REQUIREMENTS]: 'Essay Requirements',
    [JOB_TYPES.VALUE_PROPOSITION]: 'Value Proposition',
    [JOB_TYPES.STUDENT_ADVANTAGES]: 'Your Advantages',
    [JOB_TYPES.STRATEGIC_INSIGHTS]: 'Strategic Insights',
    [JOB_TYPES.HONORS_PROGRAMS]: 'Honors Programs',
    [JOB_TYPES.FINANCIAL_AID]: 'Financial Aid',
    [JOB_TYPES.CAMPUS_CULTURE]: 'Campus Culture'
  };
  return labels[jobType] || jobType;
}

/**
 * Get job type priority order
 */
export function getJobTypePriority(jobType) {
  const priorities = {
    [JOB_TYPES.COLLEGE_DATA]: 1,
    [JOB_TYPES.DEADLINES]: 2,
    [JOB_TYPES.ESSAY_REQUIREMENTS]: 3,
    [JOB_TYPES.HONORS_PROGRAMS]: 4,
    [JOB_TYPES.VALUE_PROPOSITION]: 5,
    [JOB_TYPES.CAMPUS_CULTURE]: 6,
    [JOB_TYPES.FINANCIAL_AID]: 7,
    [JOB_TYPES.STUDENT_ADVANTAGES]: 8,
    [JOB_TYPES.STRATEGIC_INSIGHTS]: 9
  };
  return priorities[jobType] || 10;
}

/**
 * Check if we should trigger immediate processing
 */
async function checkIfShouldTriggerProcessing() {
  try {
    // Get queue status from database
    const { data, error } = await supabase
      .from('queue_processing_status')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000000')
      .single();

    if (error || !data) return false;

    // If already processing, don't trigger again
    if (data.status === 'processing') return false;

    // If there are high-priority jobs, trigger immediately
    if (data.high_priority_jobs > 0) return true;

    // For regular jobs, check if we're in off-peak hours
    const hour = new Date().getHours();
    const isOffPeak = hour >= 2 && hour <= 6;

    // Only trigger regular jobs during off-peak
    return isOffPeak && data.pending_jobs > 0;
  } catch (error) {
    console.error('Error checking trigger status:', error);
    return false;
  }
}

/**
 * Trigger queue processing (fire and forget)
 */
async function triggerQueueProcessing() {
  // In development, just log
  if (process.env.NODE_ENV === 'development') {
    console.log('📬 Queue processing triggered (dev mode - manual processing required)');
    return;
  }

  // In production, call the trigger endpoint
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_URL}/api/queue`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.CRON_SECRET}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ action: 'trigger' })
    });

    if (!response.ok) {
      console.error('Failed to trigger queue processing');
    }
  } catch (error) {
    console.error('Error triggering queue:', error);
  }
}

/**
 * Get queue processing status
 */
export async function getQueueProcessingStatus() {
  try {
    const { data, error } = await supabase
      .from('queue_processing_status')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000000')
      .single();

    if (error) throw error;

    return {
      success: true,
      data: {
        ...data,
        shouldProcess: data.pending_jobs > 0,
        isHighPriority: data.high_priority_jobs > 0,
        nextProcessingTime: await getNextProcessingTime(data)
      }
    };
  } catch (error) {
    console.error('Error fetching queue status:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Calculate next processing time based on priority
 */
async function getNextProcessingTime(status) {
  // High priority = now
  if (status.high_priority_jobs > 0) {
    return new Date();
  }

  // No jobs = null
  if (status.pending_jobs === 0) {
    return null;
  }

  // Regular priority = next off-peak window
  const now = new Date();
  const hour = now.getHours();

  if (hour >= 2 && hour < 6) {
    // We're in off-peak, process now
    return now;
  } else if (hour < 2) {
    // Schedule for 2 AM today
    const next = new Date(now);
    next.setHours(2, 0, 0, 0);
    return next;
  } else {
    // Schedule for 2 AM tomorrow
    const next = new Date(now);
    next.setDate(next.getDate() + 1);
    next.setHours(2, 0, 0, 0);
    return next;
  }
}